import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { a as BusinessFormStep, B as BusinessFormStepV2, c as BusinessFormClickActions } from './business-form-types.js';
import { B as Button } from './button.js';
import { d as defineCustomElement$b } from './custom-popper2.js';
import { d as defineCustomElement$a } from './form-control-date2.js';
import { d as defineCustomElement$9 } from './form-control-error-text2.js';
import { d as defineCustomElement$8 } from './form-control-number-masked2.js';
import { d as defineCustomElement$7 } from './form-control-select2.js';
import { d as defineCustomElement$6 } from './form-control-text2.js';
import { d as defineCustomElement$5 } from './form-control-tooltip2.js';
import { d as defineCustomElement$4 } from './identity-address-form.js';
import { d as defineCustomElement$3 } from './owner-form.js';
import { d as defineCustomElement$2 } from './owner-form-core2.js';
import { d as defineCustomElement$1 } from './owner-form-inputs2.js';

const BusinessOwnersFormStepCore = /*@__PURE__*/ proxyCustomElement(class BusinessOwnersFormStepCore extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.submitted = createEvent(this, "submitted", 7);
        this.stepCompleted = createEvent(this, "form-step-completed", 7);
        this.clickEvent = createEvent(this, "click-event", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.matchRef = (ref, ownerId) => {
            const ownerIndex = this.refs.findIndex(ref => ref === ownerId);
            if (ownerIndex !== -1) {
                this.refs[ownerIndex] = ref;
            }
        };
        this.getData = () => {
            this.formLoading.emit(true);
            this.getBusiness({
                onSuccess: (response) => {
                    if (response.data.owners.length) {
                        this.ownersPayload = response.data.owners.map(owner => ({ id: owner.id }));
                    }
                    else {
                        this.addOwnerForm();
                    }
                },
                onError: ({ error, code, severity }) => {
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => {
                    this.formLoading.emit(false);
                    this.manageRefs();
                }
            });
        };
        this.sendData = async (onSuccess) => {
            let submittedData;
            this.formLoading.emit(true);
            this.patchBusiness({
                payload: { owners: this.ownersPayload },
                onSuccess: (response) => {
                    submittedData = response;
                    onSuccess();
                },
                onError: ({ error, code, severity }) => {
                    submittedData = error;
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => {
                    this.submitted.emit({ data: submittedData, metadata: { completedStep: BusinessFormStep.owners } });
                    this.stepCompleted.emit({ data: submittedData, formStep: BusinessFormStepV2.owners });
                    this.formLoading.emit(false);
                }
            });
        };
        this.addOwnerForm = (fireClick) => {
            this.newFormOpen = true;
            this.ownersPayload = [...this.ownersPayload, { id: '' }];
            fireClick && this.clickEvent.emit({ name: BusinessFormClickActions.addOwnerForm });
        };
        this.removeOwnerForm = (id) => {
            this.ownersPayload = this.ownersPayload.filter(owner => owner.id !== id);
            this.newFormOpen && (this.newFormOpen = false);
        };
        this.ownersPayload = [];
        this.refs = [];
        this.newFormOpen = undefined;
        this.authToken = undefined;
        this.businessId = undefined;
        this.getBusiness = undefined;
        this.patchBusiness = undefined;
        this.allowOptionalFields = undefined;
    }
    get showAddOwnerButton() {
        return this.ownersPayload.length < 4 && !this.newFormOpen;
    }
    manageRefs() {
        this.refs = this.ownersPayload.map((owner) => { return owner.id; });
    }
    async validateAndSubmit({ onSuccess }) {
        const formValidations = this.refs.map(ref => ref.validate());
        const formsValid = (await Promise.all(formValidations)).every(result => result);
        if (!formsValid) {
            return;
        }
        const formSubmissions = this.refs.map(ref => ref.submit());
        const submissionsValid = (await Promise.all(formSubmissions)).every(result => result);
        if (!submissionsValid) {
            return;
        }
        this.sendData(onSuccess);
    }
    ;
    componentWillLoad() {
        this.getBusiness && this.getData();
    }
    ownersWatcher() {
        this.manageRefs();
    }
    handleOwnerSubmit(event) {
        const submittedOwner = event.detail;
        const updatedOwners = this.ownersPayload.map(owner => owner.id === submittedOwner.id ? submittedOwner : owner);
        if (!updatedOwners.includes(submittedOwner)) {
            const newOwnerIndex = this.ownersPayload.findIndex(owner => !owner.id);
            updatedOwners[newOwnerIndex] = submittedOwner;
            this.newFormOpen && (this.newFormOpen = false);
        }
        this.ownersPayload = updatedOwners;
    }
    render() {
        return (h("div", { key: '54dc14b59646d1cb539fd0b58ce6635350b3df4b' }, h("div", { key: 'b33f4e979244d857de0bb33b5ef5d4c31086cc10', class: "d-flex align-items-center gap-2" }, h("legend", { key: '96a4c633e8111bcfb5dd86e6f9e08ef98ea2728d', class: "mb-0" }, "Owners"), h("form-control-tooltip", { key: '057e701996ea52daf1b8cd0dc8ee97020918d844', helpText: "For partnerships, LLCs or privately held corporations, the business is required to apply with all individuals with 25% or more ownership to the application. For charities and registered non-profits, the business is required to apply with 1 individual with substantial control over the entity, such as a board member or director." })), h("hr", { key: 'ef7dbdb63e95b95a3fa8630938a8cd1bf17855f7', class: "mt-2" }), h("div", { key: '53672f1511d6af3c4ae60572da139ba8249dd1c5', class: 'row gy-3' }, this.ownersPayload.map((owner) => {
            return (h("justifi-owner-form", { key: owner.id, authToken: this.authToken, businessId: this.businessId, ownerId: owner.id, removeOwner: this.removeOwnerForm, newFormOpen: this.newFormOpen, ownersLength: this.ownersPayload.length, allowOptionalFields: this.allowOptionalFields, ref: (ref) => this.matchRef(ref, owner.id) }));
        })), h("div", { key: '9db46d31044dca403fd6ed13e36c8315ad4ca32a', class: 'd-flex justify-content-start' }, h("div", { key: 'a55cf43f922428201e0d016d1eb10489ba9ca6aa', class: 'gap-2' }, h(Button, { key: '0393ff4837999d169afdcb5c79a67bb50176edcd', variant: 'primary', onClick: () => this.addOwnerForm(true), hidden: !this.showAddOwnerButton }, "Add Owner")))));
    }
    static get watchers() { return {
        "ownersPayload": ["ownersWatcher"]
    }; }
}, [0, "justifi-business-owners-form-step-core", {
        "authToken": [1, "auth-token"],
        "businessId": [1, "business-id"],
        "getBusiness": [16],
        "patchBusiness": [16],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "ownersPayload": [32],
        "refs": [32],
        "newFormOpen": [32],
        "validateAndSubmit": [64]
    }, [[0, "ownerSubmitted", "handleOwnerSubmit"]], {
        "ownersPayload": ["ownersWatcher"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-business-owners-form-step-core", "custom-popper", "form-control-date", "form-control-error-text", "form-control-number-masked", "form-control-select", "form-control-text", "form-control-tooltip", "justifi-identity-address-form", "justifi-owner-form", "owner-form-core", "owner-form-inputs"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-business-owners-form-step-core":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BusinessOwnersFormStepCore);
            }
            break;
        case "custom-popper":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "form-control-date":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "form-control-number-masked":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "form-control-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "justifi-identity-address-form":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "justifi-owner-form":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "owner-form-core":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "owner-form-inputs":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BusinessOwnersFormStepCore as B, defineCustomElement as d };
